import { useState, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import SetupPage from '../features/setupFlow/pages/setup/SetupPage'
import { WelcomeScreen } from '../features/setupFlow/pages/welcome/WelcomeScreen'
import { useAuth } from '../contexts/AuthProvider'
import { getPlatformAPI } from '../lib/platform'
import { LogoScreen } from '../components/common/LogoScreen'
import { navigateToHome } from './navigateToHome'

function MainTransition() {
  const navigate = useNavigate()

  useEffect(() => {
    navigateToHome(navigate)
  }, [navigate])

  return <LogoScreen />
}

export function SetupRoute() {
  const { isAuthenticated, loading } = useAuth()
  const { pathname } = useLocation()
  const [isCheckingSetupFlow, setIsCheckingSetupFlow] = useState(true)
  // First-run users see the local-first WelcomeScreen; clicking
  // "Anmelden" flips to the SetupPage's login view.
  const [showRegister, setShowRegister] = useState(false)
  // Consumed once on mount — see the forceSetupPage check below.
  const [forceSetupPage] = useState(() => {
    const flagged = sessionStorage.getItem('forceSetupPage') === 'true'
    if (flagged) sessionStorage.removeItem('forceSetupPage')
    return flagged
  })

  useEffect(() => {
    if (pathname !== '/') {
      setIsCheckingSetupFlow(false)
    }
  }, [pathname])

  // One-shot "force the setup/login page" intent (set by dev-reset and by the
  // "Connect to HappyCloud" action). Read it ONCE on mount into state so it
  // survives re-renders. Reading + removing it inline in the render body meant
  // a re-render (loading flip / isCheckingSetupFlow) consumed the flag, after
  // which the offlineSetupCompleted redirect bounced the user back into the app
  // — the SetupPage only flashed for a frame. The `&& !isAuthenticated` guard
  // releases the forced page once the user actually signs in.
  if (forceSetupPage && !isAuthenticated) {
    return <SetupPage />
  }

  // Password recovery must take precedence over the setup-flow loading spinner.
  // Otherwise an authenticated user landing on '/' after clicking a recovery
  // link would be stuck on the HappyTask-Logo screen waiting for isCheckingSetupFlow
  // to flip (which never happens on '/').
  if (localStorage.getItem('passwordRecoveryMode') === 'true') {
    return <SetupPage />
  }

  if (loading || (isAuthenticated && pathname === '/' && isCheckingSetupFlow)) {
    return (
      <LogoScreen variant="below-titlebar" />
    )
  }

  const offlineSetupCompleted = localStorage.getItem('offlineSetupCompleted') === 'true'
  const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true'

  if (offlineSetupCompleted && !isAuthenticated) {
    if (onboardingCompleted) {
      return <MainTransition />
    } else {
      return <Navigate to="/onboarding" replace />
    }
  }

  if (isAuthenticated) {
    const isPasswordRecovery = localStorage.getItem('passwordRecoveryMode') === 'true'

    if (isPasswordRecovery) {
      return <SetupPage />
    }

    if (pathname === '/') {
      setTimeout(() => setIsCheckingSetupFlow(false), 500)
      return (
        <LogoScreen variant="full" />
      )
    }
  }

  // First run (truly fresh device, no session, never registered): show the
  // local-first WelcomeScreen. Returning users (onboarding done, or have logged
  // in before) go straight to the SetupPage. Reaching here already implies
  // not authenticated and offline setup not completed.
  // Electron only — the local-first WelcomeScreen offers an offline path that
  // the cloud-only Web app cannot serve, so Web keeps the SetupPage flow.
  const isElectron = getPlatformAPI().platform === 'electron'
  const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore') === 'true'
  if (isElectron && !onboardingCompleted && !hasLoggedInBefore && !showRegister) {
    return <WelcomeScreen onRegister={() => setShowRegister(true)} />
  }

  // Coming from the WelcomeScreen CTA ("Anmelden") opens the
  // login view and offers a back link to the WelcomeScreen; returning users
  // fall through to SetupPage's smart default with no back link.
  return (
    <SetupPage
      initialView={showRegister ? 'login' : undefined}
      onBack={showRegister ? () => setShowRegister(false) : undefined}
    />
  )
}
